import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { comoFuncionaService, usersService } from '../services/api';
import '../styles/ComoFunciona.css';

const HEADER_COLUNA_A = new Set(['usuario', 'user', 'numero', 'telefone', 'login', 'did']);
const HEADER_COLUNA_B = new Set(['senha', 'password', 'pass']);

const normalizarCabecalho = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const celulaParaTexto = (cell) => {
  if (!cell || cell.v == null || cell.v === '') return '';
  if (typeof cell.v === 'number' && Number.isFinite(cell.v)) {
    if (Number.isInteger(cell.v)) return String(cell.v);
    const texto = cell.w != null ? String(cell.w) : String(cell.v);
    return texto.trim();
  }
  const texto = cell.w != null && String(cell.w).trim() !== '' ? cell.w : cell.v;
  return String(texto).trim();
};

const parsePlanilhaNumerosSenhas = (buffer) => {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet || !sheet['!ref']) return [];

  const range = XLSX.utils.decode_range(sheet['!ref']);
  const pares = [];

  for (let r = range.s.r; r <= range.e.r; r += 1) {
    const usuario = celulaParaTexto(sheet[XLSX.utils.encode_cell({ r, c: 0 })]);
    const senha = celulaParaTexto(sheet[XLSX.utils.encode_cell({ r, c: 1 })]);
    if (!usuario && !senha) continue;

    if (pares.length === 0 && r === range.s.r) {
      const cabA = normalizarCabecalho(usuario);
      const cabB = normalizarCabecalho(senha);
      if (HEADER_COLUNA_A.has(cabA) || HEADER_COLUNA_B.has(cabB)) continue;
    }

    if (!usuario || !senha) {
      throw new Error(`Linha ${r + 1} incompleta. A coluna A precisa do número e a coluna B da senha.`);
    }
    pares.push({ usuario, senha });
  }

  return pares;
};

const paresParaTexto = (pares) =>
  pares.map((p) => `Usuário\n${p.usuario}\n\nSenha\n${p.senha}`).join('\n\n');

const STATUS_ORDEM = {
  aguardando_pagamento: { label: 'Aguardando pagamento', className: 'cf-badge-warn' },
  comprovante_enviado: { label: 'Comprovante enviado', className: 'cf-badge-info' },
  aguardando_numeros: { label: 'Aguardando números', className: 'cf-badge-info' },
  concluida: { label: 'Concluída', className: 'cf-badge-ok' },
  cancelada: { label: 'Cancelada', className: 'cf-badge-off' }
};

const STATUS_NUMERO = {
  estoque_supervisor: { label: 'Estoque', className: 'cf-badge-info' },
  ativo: { label: 'Ativo', className: 'cf-badge-ok' },
  pendente_senha: { label: 'Troca de senha', className: 'cf-badge-warn' },
  cancelado: { label: 'Cancelado', className: 'cf-badge-off' }
};

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
};

const formatCnpj = (raw) => {
  const n = String(raw || '').replace(/\D/g, '');
  if (n.length !== 14) return raw;
  return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const ComoFunciona = () => {
  const { user, hasRole, isAdminOrDiretor } = useAuth();
  const podeAcessar =
    hasRole('admin', 'diretor') || user?.acesso_como_funciona === true;

  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [aba, setAba] = useState('inicio');
  const [config, setConfig] = useState(null);
  const [counts, setCounts] = useState({});
  const [numeros, setNumeros] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [estoque, setEstoque] = useState([]);
  const [subordinados, setSubordinados] = useState([]);
  const [pendentesSenha, setPendentesSenha] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [destinosManual, setDestinosManual] = useState([]);
  const [estoqueDispCount, setEstoqueDispCount] = useState(0);

  const [numPage, setNumPage] = useState(1);
  const [numTotalPages, setNumTotalPages] = useState(1);
  const [numTotal, setNumTotal] = useState(0);
  const [numStatus, setNumStatus] = useState('');
  const [numQ, setNumQ] = useState('');
  const [numQDebounced, setNumQDebounced] = useState('');
  const [numValidade, setNumValidade] = useState('');
  const [numDonoId, setNumDonoId] = useState('');

  const [estPage, setEstPage] = useState(1);
  const [estTotalPages, setEstTotalPages] = useState(1);
  const [estTotal, setEstTotal] = useState(0);
  const [estQ, setEstQ] = useState('');
  const [estQDebounced, setEstQDebounced] = useState('');
  const [estValidade, setEstValidade] = useState('vigentes');

  const [ordPage, setOrdPage] = useState(1);
  const [ordTotalPages, setOrdTotalPages] = useState(1);
  const [ordTotal, setOrdTotal] = useState(0);
  const [ordStatus, setOrdStatus] = useState('');
  const [ordQ, setOrdQ] = useState('');
  const [ordQDebounced, setOrdQDebounced] = useState('');

  const [senPage, setSenPage] = useState(1);
  const [senTotalPages, setSenTotalPages] = useState(1);
  const [senTotal, setSenTotal] = useState(0);
  const [senQ, setSenQ] = useState('');
  const [senQDebounced, setSenQDebounced] = useState('');

  const [histPage, setHistPage] = useState(1);
  const [histTotalPages, setHistTotalPages] = useState(1);
  const [histTotal, setHistTotal] = useState(0);
  const [histQ, setHistQ] = useState('');
  const [histQDebounced, setHistQDebounced] = useState('');
  const [histAcao, setHistAcao] = useState('');
  const [histParaUserId, setHistParaUserId] = useState('');
  const [historico, setHistorico] = useState([]);
  const [emUso, setEmUso] = useState([]);
  const [emUsoPage, setEmUsoPage] = useState(1);
  const [emUsoTotalPages, setEmUsoTotalPages] = useState(1);
  const [emUsoTotal, setEmUsoTotal] = useState(0);
  const [emUsoQ, setEmUsoQ] = useState('');
  const [emUsoQDebounced, setEmUsoQDebounced] = useState('');
  const [emUsoStatus, setEmUsoStatus] = useState('');
  const [emUsoDonoId, setEmUsoDonoId] = useState('');

  const [qtdCompra, setQtdCompra] = useState(1);
  const [comprando, setComprando] = useState(false);

  const [manualDestinoId, setManualDestinoId] = useState('');
  const [manualTexto, setManualTexto] = useState('');
  const [manualModo, setManualModo] = useState('auto');
  const [liberandoManual, setLiberandoManual] = useState(false);

  const [configForm, setConfigForm] = useState({
    preco_unitario: '',
    chave_pix: '',
    chave_pix_tipo: 'CNPJ',
    texto_pagina: ''
  });
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  const [comprovanteFiles, setComprovanteFiles] = useState({});
  const [enviandoComprovante, setEnviandoComprovante] = useState(null);

  const [textoNumeros, setTextoNumeros] = useState({});
  const [planilhaInfo, setPlanilhaInfo] = useState({});
  const [destinoSupervisor, setDestinoSupervisor] = useState({});
  const [cadastrando, setCadastrando] = useState(null);

  const [textoSenha, setTextoSenha] = useState({});
  const [enviandoSenha, setEnviandoSenha] = useState(null);

  const [distVendedorId, setDistVendedorId] = useState('');
  const [distQtd, setDistQtd] = useState(1);
  const [distSupervisorId, setDistSupervisorId] = useState('');
  const [usarQtd, setUsarQtd] = useState(1);
  const [distribuindo, setDistribuindo] = useState(false);
  const [usandoLote, setUsandoLote] = useState(false);
  const [selectedEmUsoIds, setSelectedEmUsoIds] = useState([]);
  const [transfDestinoId, setTransfDestinoId] = useState('');
  const [transferindo, setTransferindo] = useState(false);

  const podeTransferir = isAdminOrDiretor() || user?.role === 'supervisor';

  const totalCompra = useMemo(() => {
    const preco = parseFloat(config?.preco_unitario || 0);
    const qtd = parseInt(qtdCompra, 10) || 0;
    return Math.round(preco * qtd * 100) / 100;
  }, [config, qtdCompra]);

  const emUsoTransferiveis = useMemo(
    () => emUso.filter((n) => n.status === 'ativo' || n.status === 'pendente_senha'),
    [emUso]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await comoFuncionaService.getDashboard();
      setConfig(data.config);
      setCounts(data.counts || {});
      setEstoqueDispCount(data.counts?.estoque_disponivel || 0);
      if (user?.role === 'supervisor') {
        setSubordinados(data.subordinados || []);
      }
      setConfigForm({
        preco_unitario: data.config?.preco_unitario ?? '',
        chave_pix: data.config?.chave_pix || '51860249000108',
        chave_pix_tipo: data.config?.chave_pix_tipo || 'CNPJ',
        texto_pagina: data.config?.texto_pagina || ''
      });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao carregar página');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  const loadNumeros = useCallback(async () => {
    try {
      setListLoading(true);
      const params = {
        page: numPage,
        limit: 20
      };
      if (isAdminOrDiretor() && numDonoId === 'all') {
        params.escopo = 'todos';
      } else if (isAdminOrDiretor() && numDonoId) {
        params.escopo = 'todos';
        params.dono_id = numDonoId;
      } else {
        params.escopo = 'meus';
      }
      if (numStatus) params.status = numStatus;
      if (numQDebounced.trim()) params.q = numQDebounced.trim();
      if (numValidade) params.validade = numValidade;
      const res = await comoFuncionaService.listNumeros(params);
      setNumeros(res.data || []);
      setNumTotal(res.pagination?.total || 0);
      setNumTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar números');
    } finally {
      setListLoading(false);
    }
  }, [numPage, numStatus, numQDebounced, numValidade, numDonoId, isAdminOrDiretor]);

  const loadEstoque = useCallback(async () => {
    try {
      setListLoading(true);
      const params = {
        escopo: 'estoque',
        page: estPage,
        limit: 20
      };
      if (estQDebounced.trim()) params.q = estQDebounced.trim();
      if (estValidade) params.validade = estValidade;
      if (isAdminOrDiretor() && user?.role !== 'supervisor' && distSupervisorId) {
        params.dono_id = distSupervisorId;
      }
      const res = await comoFuncionaService.listNumeros(params);
      setEstoque(res.data || []);
      setEstTotal(res.pagination?.total || 0);
      setEstTotalPages(res.pagination?.totalPages || 1);

      const disp = await comoFuncionaService.listNumeros({
        escopo: 'estoque',
        validade: 'vigentes',
        page: 1,
        limit: 1,
        ...(isAdminOrDiretor() && user?.role !== 'supervisor' && distSupervisorId
          ? { dono_id: distSupervisorId }
          : {})
      });
      setEstoqueDispCount(disp.pagination?.total || 0);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar estoque');
    } finally {
      setListLoading(false);
    }
  }, [estPage, estQDebounced, estValidade, distSupervisorId, isAdminOrDiretor, user?.role]);

  const loadOrdens = useCallback(async () => {
    try {
      setListLoading(true);
      const isAdminLista = aba === 'admin-pedidos';
      const params = {
        page: ordPage,
        limit: 15
      };
      if (!isAdminLista) {
        params.meus = '1';
      }
      if (ordStatus) params.status = ordStatus;
      if (ordQDebounced.trim()) params.q = ordQDebounced.trim();
      const res = await comoFuncionaService.listOrdens(params);
      setOrdens(res.data || []);
      setOrdTotal(res.pagination?.total || 0);
      setOrdTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar pedidos');
    } finally {
      setListLoading(false);
    }
  }, [ordPage, ordStatus, ordQDebounced, aba]);

  const loadPendentesSenha = useCallback(async () => {
    try {
      setListLoading(true);
      const params = {
        escopo: 'pendente_senha',
        page: senPage,
        limit: 20
      };
      if (senQDebounced.trim()) params.q = senQDebounced.trim();
      const res = await comoFuncionaService.listNumeros(params);
      setPendentesSenha(res.data || []);
      setSenTotal(res.pagination?.total || 0);
      setSenTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar trocas de senha');
    } finally {
      setListLoading(false);
    }
  }, [senPage, senQDebounced]);

  const loadHistorico = useCallback(async () => {
    try {
      setListLoading(true);
      const params = {
        page: histPage,
        limit: 20
      };
      if (histQDebounced.trim()) params.q = histQDebounced.trim();
      if (histAcao) params.acao = histAcao;
      if (histParaUserId) params.para_user_id = histParaUserId;
      const res = await comoFuncionaService.listHistorico(params);
      setHistorico(res.data || []);
      setHistTotal(res.pagination?.total || 0);
      setHistTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar histórico');
    } finally {
      setListLoading(false);
    }
  }, [histPage, histQDebounced, histAcao, histParaUserId]);

  const loadEmUso = useCallback(async () => {
    try {
      setListLoading(true);
      const params = {
        escopo: 'em_uso',
        page: emUsoPage,
        limit: 20
      };
      if (emUsoQDebounced.trim()) params.q = emUsoQDebounced.trim();
      if (emUsoStatus) params.status = emUsoStatus;
      if (emUsoDonoId) params.dono_id = emUsoDonoId;
      const res = await comoFuncionaService.listNumeros(params);
      setEmUso(res.data || []);
      setEmUsoTotal(res.pagination?.total || 0);
      setEmUsoTotalPages(res.pagination?.totalPages || 1);
      setSelectedEmUsoIds([]);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao listar chips em uso');
    } finally {
      setListLoading(false);
    }
  }, [emUsoPage, emUsoQDebounced, emUsoStatus, emUsoDonoId]);

  const reloadListaAtual = useCallback(async () => {
    await load();
    if (aba === 'numeros') await loadNumeros();
    else if (aba === 'estoque') await loadEstoque();
    else if (aba === 'pedidos' || aba === 'admin-pedidos') await loadOrdens();
    else if (aba === 'admin-senhas') await loadPendentesSenha();
    else if (aba === 'historico') await loadHistorico();
    else if (aba === 'em-uso') await loadEmUso();
  }, [aba, load, loadNumeros, loadEstoque, loadOrdens, loadPendentesSenha, loadHistorico, loadEmUso]);

  useEffect(() => {
    const t = setTimeout(() => setNumQDebounced(numQ), 350);
    return () => clearTimeout(t);
  }, [numQ]);
  useEffect(() => {
    const t = setTimeout(() => setEstQDebounced(estQ), 350);
    return () => clearTimeout(t);
  }, [estQ]);
  useEffect(() => {
    const t = setTimeout(() => setOrdQDebounced(ordQ), 350);
    return () => clearTimeout(t);
  }, [ordQ]);
  useEffect(() => {
    const t = setTimeout(() => setSenQDebounced(senQ), 350);
    return () => clearTimeout(t);
  }, [senQ]);
  useEffect(() => {
    const t = setTimeout(() => setHistQDebounced(histQ), 350);
    return () => clearTimeout(t);
  }, [histQ]);
  useEffect(() => {
    const t = setTimeout(() => setEmUsoQDebounced(emUsoQ), 350);
    return () => clearTimeout(t);
  }, [emUsoQ]);

  useEffect(() => {
    if (podeAcessar) load();
  }, [podeAcessar, load]);

  useEffect(() => {
    if (!podeAcessar) return;
    if (aba === 'numeros') loadNumeros();
    else if (aba === 'estoque') loadEstoque();
    else if (aba === 'pedidos' || aba === 'admin-pedidos') loadOrdens();
    else if (aba === 'admin-senhas') loadPendentesSenha();
    else if (aba === 'historico') loadHistorico();
    else if (aba === 'em-uso') loadEmUso();
  }, [aba, podeAcessar, loadNumeros, loadEstoque, loadOrdens, loadPendentesSenha, loadHistorico, loadEmUso]);

  useEffect(() => {
    if (!isAdminOrDiretor()) return;
    usersService
      .getAll()
      .then((list) => {
        const arr = Array.isArray(list) ? list : list?.users || list?.data || [];
        setSupervisores(arr.filter((u) => u.role === 'supervisor' && u.ativo !== false));
        const destinos = arr.filter(
          (u) =>
            u.ativo !== false &&
            ['vendedor', 'supervisor', 'admin', 'diretor'].includes(u.role) &&
            (['admin', 'diretor'].includes(u.role) || u.acesso_como_funciona)
        );
        setDestinosManual(destinos);
        if (isAdminOrDiretor()) {
          setSubordinados(
            arr.filter(
              (u) => u.role === 'vendedor' && u.ativo !== false && u.acesso_como_funciona
            )
          );
        }
      })
      .catch(() => setSupervisores([]));
  }, [isAdminOrDiretor]);

  if (!podeAcessar) {
    return <Navigate to="/dashboard" replace />;
  }

  const renderPagination = (page, totalPages, total, setPage) => (
    <div className="cf-pagination">
      <button
        type="button"
        className="cf-btn-secondary cf-btn-sm"
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      >
        Anterior
      </button>
      <span>
        Página {page} de {totalPages} ({total} registro{total === 1 ? '' : 's'})
      </span>
      <button
        type="button"
        className="cf-btn-secondary cf-btn-sm"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Próxima
      </button>
    </div>
  );

  const handleComprar = async (e) => {
    e.preventDefault();
    const qtd = parseInt(qtdCompra, 10);
    if (!qtd || qtd < 1) {
      alert('Informe uma quantidade válida');
      return;
    }
    try {
      setComprando(true);
      await comoFuncionaService.criarOrdem(qtd);
      alert('Pedido criado! Transfira o valor na chave PIX e anexe o comprovante.');
      setQtdCompra(1);
      setAba('pedidos');
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar pedido');
    } finally {
      setComprando(false);
    }
  };

  const handleSalvarConfig = async (e) => {
    e.preventDefault();
    try {
      setSalvandoConfig(true);
      const updated = await comoFuncionaService.updateConfig({
        preco_unitario: parseFloat(configForm.preco_unitario),
        chave_pix: configForm.chave_pix,
        chave_pix_tipo: configForm.chave_pix_tipo,
        texto_pagina: configForm.texto_pagina
      });
      setConfig(updated);
      alert('Configuração salva!');
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar configuração');
    } finally {
      setSalvandoConfig(false);
    }
  };

  const handleEnviarComprovante = async (ordemId) => {
    const file = comprovanteFiles[ordemId];
    if (!file) {
      alert('Selecione o comprovante (PDF ou imagem)');
      return;
    }
    try {
      setEnviandoComprovante(ordemId);
      await comoFuncionaService.enviarComprovante(ordemId, file);
      alert('Comprovante enviado! Aguarde a confirmação do pagamento.');
      setComprovanteFiles((prev) => {
        const next = { ...prev };
        delete next[ordemId];
        return next;
      });
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao enviar comprovante');
    } finally {
      setEnviandoComprovante(null);
    }
  };

  const handleConfirmarPago = async (ordemId) => {
    if (!window.confirm('Confirmar que o pagamento foi recebido?')) return;
    try {
      await comoFuncionaService.confirmarPago(ordemId);
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao confirmar pagamento');
    }
  };

  const handleCancelarOrdem = async (ordemId) => {
    if (!window.confirm('Cancelar este pedido?')) return;
    try {
      await comoFuncionaService.cancelarOrdem(ordemId);
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar');
    }
  };

  const handlePlanilhaNumeros = async (ordemId, quantidade, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      alert('Envie uma planilha Excel no formato .xlsx');
      return;
    }

    try {
      const pares = parsePlanilhaNumerosSenhas(await file.arrayBuffer());
      if (pares.length === 0) {
        alert('Nenhum par encontrado. Use a coluna A para o número e a coluna B para a senha.');
        return;
      }

      setTextoNumeros((prev) => ({ ...prev, [ordemId]: paresParaTexto(pares) }));
      setPlanilhaInfo((prev) => ({
        ...prev,
        [ordemId]: `${pares.length} par(es) lido(s) de ${file.name}`
      }));

      if (pares.length !== quantidade) {
        alert(
          `A planilha tem ${pares.length} par(es) e este pedido pede ${quantidade}. Confira antes de cadastrar.`
        );
      }
    } catch (err) {
      alert(err.message || 'Não foi possível ler a planilha');
    }
  };

  const handleCadastrarNumeros = async (ordemId, quantidade) => {
    const texto = textoNumeros[ordemId] || '';
    try {
      setCadastrando(ordemId);
      await comoFuncionaService.cadastrarNumeros(
        ordemId,
        texto,
        destinoSupervisor[ordemId] || null
      );
      alert(`${quantidade} número(s) cadastrado(s)!`);
      setTextoNumeros((prev) => {
        const next = { ...prev };
        delete next[ordemId];
        return next;
      });
      setPlanilhaInfo((prev) => {
        const next = { ...prev };
        delete next[ordemId];
        return next;
      });
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cadastrar números');
    } finally {
      setCadastrando(null);
    }
  };

  const handleUsar = async (id) => {
    try {
      await comoFuncionaService.usarNumero(id);
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao ativar número');
    }
  };

  const handleDistribuir = async (e) => {
    e.preventDefault();
    const qtd = parseInt(distQtd, 10);
    if (!distVendedorId) {
      alert('Selecione o vendedor');
      return;
    }
    if (!qtd || qtd < 1) {
      alert('Informe a quantidade');
      return;
    }
    if (isAdminOrDiretor() && user?.role !== 'supervisor' && !distSupervisorId) {
      alert('Selecione o supervisor dono do estoque');
      return;
    }
    try {
      setDistribuindo(true);
      const result = await comoFuncionaService.distribuirNumeros({
        vendedor_id: distVendedorId,
        quantidade: qtd,
        supervisor_id:
          user?.role === 'supervisor'
            ? undefined
            : distSupervisorId || undefined
      });
      alert(
        `${result.quantidade} número(s) enviados para ${result.vendedor?.nome}. Estoque restante: ${result.estoque_restante}`
      );
      setDistQtd(1);
      setDistVendedorId('');
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao distribuir');
    } finally {
      setDistribuindo(false);
    }
  };

  const toggleEmUsoSelect = (id) => {
    setSelectedEmUsoIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const todosEmUsoPaginaSelecionados =
    emUsoTransferiveis.length > 0 &&
    emUsoTransferiveis.every((n) => selectedEmUsoIds.includes(n.id));

  const toggleTodosEmUsoPagina = () => {
    if (todosEmUsoPaginaSelecionados) {
      const idsPagina = new Set(emUsoTransferiveis.map((n) => n.id));
      setSelectedEmUsoIds((prev) => prev.filter((id) => !idsPagina.has(id)));
    } else {
      setSelectedEmUsoIds((prev) => {
        const set = new Set(prev);
        emUsoTransferiveis.forEach((n) => set.add(n.id));
        return [...set];
      });
    }
  };

  const handleTransferir = async (e) => {
    e.preventDefault();
    if (selectedEmUsoIds.length === 0) {
      alert('Selecione ao menos um chip');
      return;
    }
    if (!transfDestinoId) {
      alert('Selecione o vendedor destino');
      return;
    }
    try {
      setTransferindo(true);
      const result = await comoFuncionaService.transferirNumeros({
        numero_ids: selectedEmUsoIds,
        para_vendedor_id: parseInt(transfDestinoId, 10)
      });
      alert(
        `${result.quantidade} chip(s) transferidos para ${result.vendedor?.nome}`
      );
      setSelectedEmUsoIds([]);
      setTransfDestinoId('');
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao transferir');
    } finally {
      setTransferindo(false);
    }
  };

  const handleUsarLote = async (e) => {
    e.preventDefault();
    const qtd = parseInt(usarQtd, 10) || 1;
    if (isAdminOrDiretor() && user?.role !== 'supervisor' && !distSupervisorId) {
      alert('Selecione o supervisor dono do estoque');
      return;
    }
    try {
      setUsandoLote(true);
      const result = await comoFuncionaService.usarLote({
        quantidade: qtd,
        supervisor_id:
          user?.role === 'supervisor' ? undefined : distSupervisorId || undefined
      });
      alert(`${result.quantidade} número(s) ativado(s) para você.`);
      setUsarQtd(1);
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao usar do estoque');
    } finally {
      setUsandoLote(false);
    }
  };

  const handleSolicitarSenha = async (id) => {
    if (!window.confirm('Solicitar troca de senha? Ficará pendente para admin/diretor.')) return;
    try {
      await comoFuncionaService.solicitarSenha(id);
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao solicitar senha');
    }
  };

  const handleAtualizarSenha = async (id) => {
    const texto = textoSenha[id] || '';
    try {
      setEnviandoSenha(id);
      await comoFuncionaService.atualizarSenha(id, { texto });
      alert('Senha atualizada!');
      setTextoSenha((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar senha');
    } finally {
      setEnviandoSenha(null);
    }
  };

  const handleLiberarManual = async (e) => {
    e.preventDefault();
    if (!manualDestinoId) {
      alert('Selecione o usuário de destino');
      return;
    }
    if (!manualTexto.trim()) {
      alert('Cole os números no formato Usuário / Senha');
      return;
    }
    try {
      setLiberandoManual(true);
      const result = await comoFuncionaService.liberarManual({
        destino_id: manualDestinoId,
        texto: manualTexto,
        modo: manualModo === 'ativo' ? 'ativo' : undefined
      });
      alert(
        `${result.quantidade} número(s) liberado(s) para ${result.destino?.nome}` +
          (result.modo === 'estoque_supervisor'
            ? ' (estoque do supervisor, 14 dias a partir de agora).'
            : ' (ativos por 14 dias).')
      );
      setManualTexto('');
      setManualDestinoId('');
      setManualModo('auto');
      await reloadListaAtual();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao liberar números');
    } finally {
      setLiberandoManual(false);
    }
  };

  const tabs = [
    { id: 'inicio', label: 'Sobre' },
    { id: 'comprar', label: 'Comprar' },
    { id: 'pedidos', label: 'Meus pedidos' },
    { id: 'numeros', label: 'Meus números' },
    { id: 'em-uso', label: 'Em uso' },
    { id: 'historico', label: 'Histórico' }
  ];
  if (user?.role === 'supervisor' || isAdminOrDiretor()) {
    tabs.push({ id: 'estoque', label: 'Estoque' });
  }
  if (isAdminOrDiretor()) {
    tabs.push({ id: 'admin-manual', label: 'Liberar manual' });
    tabs.push({ id: 'admin-pedidos', label: 'Fila admin' });
    tabs.push({ id: 'admin-senhas', label: 'Trocas de senha' });
    tabs.push({ id: 'config', label: 'Configuração' });
  }

  const renderPedidoCard = (ordem, adminView = false) => {
    const st = STATUS_ORDEM[ordem.status] || { label: ordem.status, className: '' };
    const podeAnexar =
      !adminView &&
      ['aguardando_pagamento', 'comprovante_enviado'].includes(ordem.status) &&
      ordem.solicitante_id === user.id;
    const podeConfirmar = adminView && ordem.status === 'comprovante_enviado';
    const podeCadastrar = adminView && ordem.status === 'aguardando_numeros';

    return (
      <div key={ordem.id} className="cf-pedido-card">
        <div className="cf-pedido-header">
          <div>
            <strong>Pedido #{ordem.id}</strong>
            {adminView && ordem.solicitante && (
              <span className="cf-muted"> — {ordem.solicitante.nome} ({ordem.solicitante.role})</span>
            )}
          </div>
          <span className={`cf-badge ${st.className}`}>{st.label}</span>
        </div>

        <div className="cf-pedido-grid">
          <div>
            <label>Quantidade</label>
            <span>{ordem.quantidade}</span>
          </div>
          <div>
            <label>Preço unitário</label>
            <span>{formatMoney(ordem.preco_unitario)}</span>
          </div>
          <div>
            <label>Total</label>
            <span className="cf-total">{formatMoney(ordem.valor_total)}</span>
          </div>
          <div>
            <label>Criado em</label>
            <span>{formatDateTime(ordem.created_at)}</span>
          </div>
        </div>

        {['aguardando_pagamento', 'comprovante_enviado'].includes(ordem.status) && (
          <div className="cf-pix-box">
            <p>
              Transfira <strong>{formatMoney(ordem.valor_total)}</strong> na chave PIX{' '}
              <strong>{config?.chave_pix_tipo || 'CNPJ'}</strong>{' '}
              <code>{formatCnpj(config?.chave_pix || '51860249000108')}</code> e envie o comprovante.
            </p>
            <button
              type="button"
              className="cf-btn-secondary cf-btn-sm"
              onClick={() => {
                navigator.clipboard?.writeText(String(config?.chave_pix || '51860249000108').replace(/\D/g, '') || config?.chave_pix);
                alert('Chave PIX copiada!');
              }}
            >
              Copiar chave PIX
            </button>
          </div>
        )}

        {ordem.comprovante_path && (
          <div className="cf-comprovante-link">
            <a
              href={comoFuncionaService.getComprovanteUrl(ordem.comprovante_path)}
              target="_blank"
              rel="noreferrer"
            >
              Ver comprovante anexado
            </a>
            {ordem.comprovante_enviado_em && (
              <span className="cf-muted"> · {formatDateTime(ordem.comprovante_enviado_em)}</span>
            )}
          </div>
        )}

        {podeAnexar && (
          <div className="cf-upload-row">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
              onChange={(e) =>
                setComprovanteFiles((prev) => ({
                  ...prev,
                  [ordem.id]: e.target.files?.[0] || null
                }))
              }
            />
            <button
              type="button"
              className="cf-btn-primary"
              disabled={enviandoComprovante === ordem.id}
              onClick={() => handleEnviarComprovante(ordem.id)}
            >
              {enviandoComprovante === ordem.id ? 'Enviando...' : 'Já paguei (enviar comprovante)'}
            </button>
          </div>
        )}

        {adminView && (
          <div className="cf-admin-actions">
            {podeConfirmar && (
              <button type="button" className="cf-btn-primary" onClick={() => handleConfirmarPago(ordem.id)}>
                Confirmar pagamento
              </button>
            )}
            {!['concluida', 'cancelada'].includes(ordem.status) && (
              <button type="button" className="cf-btn-danger" onClick={() => handleCancelarOrdem(ordem.id)}>
                Cancelar
              </button>
            )}
          </div>
        )}

        {podeCadastrar && (
          <div className="cf-cadastrar-box">
            <label>Subir planilha Excel (.xlsx)</label>
            <p className="cf-muted cf-planilha-hint">
              Coluna A = número, coluna B = senha. A primeira linha pode ser cabeçalho.
            </p>
            <div className="cf-upload-row">
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handlePlanilhaNumeros(ordem.id, ordem.quantidade, e)}
              />
            </div>
            {planilhaInfo[ordem.id] && (
              <p className="cf-planilha-ok">{planilhaInfo[ordem.id]}</p>
            )}
            <label>Ou colar números (Usuário / Senha)</label>
            <textarea
              rows={8}
              placeholder={'Usuário\n4831981705\n\nSenha\n9srpt'}
              value={textoNumeros[ordem.id] || ''}
              onChange={(e) =>
                setTextoNumeros((prev) => ({ ...prev, [ordem.id]: e.target.value }))
              }
            />
            <div className="cf-form-row">
              <label>Destinar ao estoque do supervisor (opcional)</label>
              <select
                value={destinoSupervisor[ordem.id] || ''}
                onChange={(e) =>
                  setDestinoSupervisor((prev) => ({
                    ...prev,
                    [ordem.id]: e.target.value || null
                  }))
                }
              >
                <option value="">Usar solicitante ({ordem.solicitante?.nome})</option>
                {supervisores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="cf-btn-primary"
              disabled={cadastrando === ordem.id}
              onClick={() => handleCadastrarNumeros(ordem.id, ordem.quantidade)}
            >
              {cadastrando === ordem.id ? 'Salvando...' : `Cadastrar ${ordem.quantidade} número(s)`}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderNumeroRow = (n, { estoqueMode = false } = {}) => {
    const st = STATUS_NUMERO[n.status] || { label: n.status, className: '' };
    const senhaOculta = n.status === 'cancelado' || n.senha == null;

    return (
      <tr key={n.id}>
        <td>{n.usuario}</td>
        <td>
          {senhaOculta ? (
            <span className="cf-muted">••••••</span>
          ) : (
            <code>{n.senha}</code>
          )}
        </td>
        <td>
          <span className={`cf-badge ${st.className}`}>{st.label}</span>
        </td>
        <td>{n.disponivel_ate ? formatDateTime(n.disponivel_ate) : '—'}</td>
        {isAdminOrDiretor() && <td>{n.dono?.nome || n.dono_id}</td>}
        <td>
          <div className="cf-row-actions">
            {estoqueMode && (
              <button type="button" className="cf-btn-secondary cf-btn-sm" onClick={() => handleUsar(n.id)}>
                Usar este
              </button>
            )}
            {!estoqueMode && n.status === 'ativo' && n.dono_id === user.id && (
              <button
                type="button"
                className="cf-btn-secondary cf-btn-sm"
                onClick={() => handleSolicitarSenha(n.id)}
              >
                Alterar senha
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Layout>
      <div className="cf-container">
        <div className="cf-header">
          <div>
            <h1 className="cf-title">☎ DID</h1>
            <p className="cf-subtitle">Compra e gestão de números virtuais</p>
          </div>
        </div>

        <div className="cf-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`cf-tab ${aba === t.id ? 'active' : ''}`}
              onClick={() => setAba(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="cf-card">
            <div className="cf-card-body">Carregando...</div>
          </div>
        ) : (
          <>
            {aba === 'inicio' && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Informações</span>
                </div>
                <div className="cf-card-body">
                  <div className="cf-texto-pagina">
                    {(config?.texto_pagina || '').split('\n').map((line, i) => (
                      <p key={i}>{line || '\u00A0'}</p>
                    ))}
                  </div>
                  <div className="cf-info-grid">
                    <div>
                      <label>Preço unitário</label>
                      <strong>{formatMoney(config?.preco_unitario)}</strong>
                    </div>
                    <div>
                      <label>Chave PIX</label>
                      <strong>
                        {config?.chave_pix_tipo} {formatCnpj(config?.chave_pix)}
                      </strong>
                    </div>
                    <div>
                      <label>Meus números</label>
                      <strong>{counts.meus_numeros ?? 0}</strong>
                    </div>
                    <div>
                      <label>Pedidos abertos</label>
                      <strong>{counts.pedidos_abertos ?? 0}</strong>
                    </div>
                    {(user?.role === 'supervisor' || isAdminOrDiretor()) && (
                      <div>
                        <label>Estoque disponível</label>
                        <strong>{counts.estoque_disponivel ?? 0}</strong>
                      </div>
                    )}
                    {isAdminOrDiretor() && (
                      <div>
                        <label>Trocas de senha</label>
                        <strong>{counts.pendentes_senha ?? 0}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {aba === 'comprar' && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Comprar números</span>
                </div>
                <div className="cf-card-body">
                  <form className="cf-compra-form" onSubmit={handleComprar}>
                    <div className="cf-form-group">
                      <label>Quantidade</label>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        value={qtdCompra}
                        onChange={(e) => setQtdCompra(e.target.value)}
                        required
                      />
                    </div>
                    <div className="cf-soma-box">
                      <div>
                        {qtdCompra || 0} × {formatMoney(config?.preco_unitario)} ={' '}
                        <strong>{formatMoney(totalCompra)}</strong>
                      </div>
                      <p className="cf-muted">
                        Após criar o pedido, transfira o valor na chave PIX{' '}
                        <strong>{config?.chave_pix_tipo}</strong>{' '}
                        <code>{formatCnpj(config?.chave_pix)}</code> e anexe o comprovante.
                      </p>
                    </div>
                    <button type="submit" className="cf-btn-primary" disabled={comprando}>
                      {comprando ? 'Criando...' : 'Criar pedido'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {aba === 'pedidos' && (
              <div className="cf-lista">
                <div className="cf-card">
                  <div className="cf-card-body">
                    <div className="cf-filters">
                      <input
                        type="search"
                        placeholder="Buscar ID ou nome..."
                        value={ordQ}
                        onChange={(e) => {
                          setOrdQ(e.target.value);
                          setOrdPage(1);
                        }}
                      />
                      <select
                        value={ordStatus}
                        onChange={(e) => {
                          setOrdStatus(e.target.value);
                          setOrdPage(1);
                        }}
                      >
                        <option value="">Todos os status</option>
                        {Object.entries(STATUS_ORDEM).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {listLoading && <p className="cf-muted">Carregando...</p>}
                {!listLoading && ordens.length === 0 && (
                  <div className="cf-card">
                    <div className="cf-card-body cf-empty">Nenhum pedido encontrado.</div>
                  </div>
                )}
                {ordens.map((o) => renderPedidoCard(o, false))}
                {ordens.length > 0 &&
                  renderPagination(ordPage, ordTotalPages, ordTotal, setOrdPage)}
              </div>
            )}

            {aba === 'numeros' && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Meus números</span>
                </div>
                <div className="cf-card-body">
                  <div className="cf-filters">
                    <input
                      type="search"
                      placeholder="Buscar número (usuário)..."
                      value={numQ}
                      onChange={(e) => {
                        setNumQ(e.target.value);
                        setNumPage(1);
                      }}
                    />
                    <select
                      value={numStatus}
                      onChange={(e) => {
                        setNumStatus(e.target.value);
                        setNumPage(1);
                      }}
                    >
                      <option value="">Todos os status</option>
                      <option value="ativo">Ativo</option>
                      <option value="pendente_senha">Troca de senha</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                    <select
                      value={numValidade}
                      onChange={(e) => {
                        setNumValidade(e.target.value);
                        setNumPage(1);
                      }}
                    >
                      <option value="">Validade: todas</option>
                      <option value="vigentes">Vigentes</option>
                      <option value="expirados">Expirados</option>
                    </select>
                    {isAdminOrDiretor() && (
                      <select
                        value={numDonoId}
                        onChange={(e) => {
                          setNumDonoId(e.target.value);
                          setNumPage(1);
                        }}
                      >
                        <option value="">Só meus</option>
                        <option value="all">Todos os usuários</option>
                        {destinosManual.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.nome} ({u.role})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {listLoading ? (
                    <p className="cf-muted">Carregando...</p>
                  ) : numeros.length === 0 ? (
                    <p className="cf-empty">Nenhum número encontrado.</p>
                  ) : (
                    <>
                      <div className="cf-table-wrap">
                        <table className="cf-table">
                          <thead>
                            <tr>
                              <th>Usuário</th>
                              <th>Senha</th>
                              <th>Status</th>
                              <th>Válido até</th>
                              {isAdminOrDiretor() && <th>Dono</th>}
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>{numeros.map((n) => renderNumeroRow(n))}</tbody>
                        </table>
                      </div>
                      {renderPagination(numPage, numTotalPages, numTotal, setNumPage)}
                    </>
                  )}
                </div>
              </div>
            )}

            {aba === 'em-uso' && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Chips em uso</span>
                </div>
                <div className="cf-card-body">
                  <p className="cf-muted" style={{ marginBottom: '1rem' }}>
                    Mostra chips atualmente atribuídos (ativo, estoque ou troca de senha). Cancelados não
                    aparecem.
                    {podeTransferir
                      ? ' Marque chips ativos ou em troca de senha para transferir a outro vendedor.'
                      : ''}
                  </p>
                  <div className="cf-filters">
                    <input
                      type="search"
                      placeholder="Buscar número..."
                      value={emUsoQ}
                      onChange={(e) => {
                        setEmUsoQ(e.target.value);
                        setEmUsoPage(1);
                      }}
                    />
                    <select
                      value={emUsoStatus}
                      onChange={(e) => {
                        setEmUsoStatus(e.target.value);
                        setEmUsoPage(1);
                      }}
                    >
                      <option value="">Todos (exceto cancelado)</option>
                      <option value="ativo">Ativo</option>
                      <option value="estoque_supervisor">Estoque supervisor</option>
                      <option value="pendente_senha">Troca de senha</option>
                    </select>
                    {(isAdminOrDiretor() || user?.role === 'supervisor') && (
                      <select
                        value={emUsoDonoId}
                        onChange={(e) => {
                          setEmUsoDonoId(e.target.value);
                          setEmUsoPage(1);
                        }}
                      >
                        <option value="">Com quem: todos</option>
                        {isAdminOrDiretor()
                          ? destinosManual.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.nome} ({u.role})
                              </option>
                            ))
                          : subordinados.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.nome}
                              </option>
                            ))}
                        {user?.role === 'supervisor' && (
                          <option value={user.id}>Eu (supervisor)</option>
                        )}
                      </select>
                    )}
                  </div>
                  {podeTransferir && selectedEmUsoIds.length > 0 && (
                    <form className="cf-dist-form cf-transfer-bar" onSubmit={handleTransferir}>
                      <span className="cf-transfer-count">
                        {selectedEmUsoIds.length} selecionado
                        {selectedEmUsoIds.length === 1 ? '' : 's'}
                      </span>
                      <div className="cf-form-group">
                        <label>Transferir para</label>
                        <select
                          value={transfDestinoId}
                          onChange={(e) => setTransfDestinoId(e.target.value)}
                          required
                        >
                          <option value="">Selecione o vendedor...</option>
                          {subordinados.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="submit"
                        className="cf-btn-primary"
                        disabled={transferindo || !transfDestinoId}
                      >
                        {transferindo ? 'Transferindo...' : 'Transferir'}
                      </button>
                      <button
                        type="button"
                        className="cf-btn-secondary"
                        onClick={() => setSelectedEmUsoIds([])}
                        disabled={transferindo}
                      >
                        Limpar
                      </button>
                    </form>
                  )}
                  {listLoading ? (
                    <p className="cf-muted">Carregando...</p>
                  ) : emUso.length === 0 ? (
                    <p className="cf-empty">Nenhum chip em uso.</p>
                  ) : (
                    <>
                      <div className="cf-table-wrap">
                        <table className="cf-table">
                          <thead>
                            <tr>
                              {podeTransferir && (
                                <th className="cf-th-check">
                                  <input
                                    type="checkbox"
                                    checked={todosEmUsoPaginaSelecionados}
                                    onChange={toggleTodosEmUsoPagina}
                                    disabled={emUsoTransferiveis.length === 0}
                                    title="Selecionar todos da página"
                                    aria-label="Selecionar todos da página"
                                  />
                                </th>
                              )}
                              <th>Chip</th>
                              <th>Senha</th>
                              <th>Status</th>
                              <th>Com quem</th>
                              <th>Supervisor origem</th>
                              <th>Válido até</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emUso.map((n) => {
                              const st = STATUS_NUMERO[n.status] || {
                                label: n.status,
                                className: ''
                              };
                              const podeMarcar =
                                n.status === 'ativo' || n.status === 'pendente_senha';
                              return (
                                <tr key={n.id}>
                                  {podeTransferir && (
                                    <td className="cf-td-check">
                                      <input
                                        type="checkbox"
                                        checked={selectedEmUsoIds.includes(n.id)}
                                        onChange={() => toggleEmUsoSelect(n.id)}
                                        disabled={!podeMarcar}
                                        title={
                                          podeMarcar
                                            ? 'Selecionar para transferir'
                                            : 'Só chips ativos ou em troca de senha'
                                        }
                                        aria-label={`Selecionar chip ${n.usuario}`}
                                      />
                                    </td>
                                  )}
                                  <td>
                                    <code>{n.usuario}</code>
                                  </td>
                                  <td>
                                    {n.senha == null ? (
                                      <span className="cf-muted">••••••</span>
                                    ) : (
                                      <code>{n.senha}</code>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`cf-badge ${st.className}`}>{st.label}</span>
                                  </td>
                                  <td>
                                    {n.dono?.nome || n.dono_id}
                                    {n.dono?.role ? (
                                      <span className="cf-muted"> ({n.dono.role})</span>
                                    ) : null}
                                  </td>
                                  <td>{n.origemSupervisor?.nome || '—'}</td>
                                  <td>
                                    {n.disponivel_ate ? formatDateTime(n.disponivel_ate) : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {renderPagination(emUsoPage, emUsoTotalPages, emUsoTotal, setEmUsoPage)}
                    </>
                  )}
                </div>
              </div>
            )}

            {aba === 'historico' && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Histórico de atribuições</span>
                </div>
                <div className="cf-card-body">
                  <p className="cf-muted" style={{ marginBottom: '1rem' }}>
                    Registro de liberações e atribuições. Vendedor vê o que recebeu; supervisor vê a equipe;
                    admin/diretor vê tudo.
                  </p>
                  <div className="cf-filters">
                    <input
                      type="search"
                      placeholder="Buscar chip..."
                      value={histQ}
                      onChange={(e) => {
                        setHistQ(e.target.value);
                        setHistPage(1);
                      }}
                    />
                    <select
                      value={histAcao}
                      onChange={(e) => {
                        setHistAcao(e.target.value);
                        setHistPage(1);
                      }}
                    >
                      <option value="">Todas as ações</option>
                      <option value="atribuicao">Atribuição (supervisor → vendedor)</option>
                      <option value="transferencia">Transferência (vendedor → vendedor)</option>
                      <option value="liberacao_manual">Liberação manual</option>
                      <option value="liberacao_pedido">Liberação por pedido</option>
                      <option value="uso_proprio">Uso próprio (estoque)</option>
                    </select>
                    {(isAdminOrDiretor() || user?.role === 'supervisor') && (
                      <select
                        value={histParaUserId}
                        onChange={(e) => {
                          setHistParaUserId(e.target.value);
                          setHistPage(1);
                        }}
                      >
                        <option value="">Destinatário: todos</option>
                        {isAdminOrDiretor()
                          ? destinosManual.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.nome} ({u.role})
                              </option>
                            ))
                          : subordinados.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.nome}
                              </option>
                            ))}
                      </select>
                    )}
                  </div>
                  {listLoading ? (
                    <p className="cf-muted">Carregando...</p>
                  ) : historico.length === 0 ? (
                    <p className="cf-empty">Nenhum registro no histórico.</p>
                  ) : (
                    <>
                      <div className="cf-table-wrap">
                        <table className="cf-table">
                          <thead>
                            <tr>
                              <th>Data</th>
                              <th>Chip</th>
                              <th>Ação</th>
                              <th>De</th>
                              <th>Para</th>
                              <th>Por</th>
                              <th>Lote</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historico.map((h) => {
                              const acaoLabel = {
                                atribuicao: 'Atribuição',
                                transferencia: 'Transferência',
                                liberacao_manual: 'Liberação manual',
                                liberacao_pedido: 'Liberação pedido',
                                uso_proprio: 'Uso próprio'
                              }[h.acao] || h.acao;
                              return (
                                <tr key={h.id}>
                                  <td>{formatDateTime(h.created_at)}</td>
                                  <td>
                                    <code>{h.usuario_chip}</code>
                                  </td>
                                  <td>{acaoLabel}</td>
                                  <td>
                                    {h.deUser
                                      ? `${h.deUser.nome} (${h.deUser.role})`
                                      : '—'}
                                  </td>
                                  <td>
                                    {h.paraUser
                                      ? `${h.paraUser.nome} (${h.paraUser.role})`
                                      : h.para_user_id}
                                  </td>
                                  <td>{h.actor?.nome || '—'}</td>
                                  <td>{h.quantidade_lote || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {renderPagination(histPage, histTotalPages, histTotal, setHistPage)}
                    </>
                  )}
                </div>
              </div>
            )}

            {aba === 'estoque' && (
              <>
                <div className="cf-card">
                  <div className="cf-card-header">
                    <span className="cf-card-header-title">Atribuir do estoque</span>
                  </div>
                  <div className="cf-card-body">
                    <p className="cf-muted" style={{ marginBottom: '1rem' }}>
                      Estoque disponível: <strong>{estoqueDispCount}</strong>. Escolha o
                      vendedor e a quantidade — os números saem do estoque e vão para o vendedor
                      (prazo de 14 dias não reinicia).
                    </p>
                    {isAdminOrDiretor() && user?.role !== 'supervisor' && (
                      <div className="cf-form-group" style={{ marginBottom: '1rem', maxWidth: 420 }}>
                        <label>Supervisor (dono do estoque)</label>
                        <select
                          value={distSupervisorId}
                          onChange={(e) => {
                            setDistSupervisorId(e.target.value);
                            setEstPage(1);
                          }}
                        >
                          <option value="">Selecione...</option>
                          {supervisores.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <form className="cf-dist-form" onSubmit={handleDistribuir}>
                      <div className="cf-form-group">
                        <label>Vendedor</label>
                        <select
                          value={distVendedorId}
                          onChange={(e) => setDistVendedorId(e.target.value)}
                          required
                        >
                          <option value="">Selecione...</option>
                          {subordinados
                            .filter((v) =>
                              user?.role === 'supervisor'
                                ? true
                                : !distSupervisorId ||
                                  String(v.supervisor_id) === String(distSupervisorId)
                            )
                            .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.nome}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="cf-form-group">
                        <label>Quantidade</label>
                        <input
                          type="number"
                          min={1}
                          max={estoqueDispCount || 1}
                          value={distQtd}
                          onChange={(e) => setDistQtd(e.target.value)}
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="cf-btn-primary"
                        disabled={distribuindo || estoqueDispCount === 0}
                      >
                        {distribuindo ? 'Enviando...' : 'Atribuir ao vendedor'}
                      </button>
                    </form>

                    {(user?.role === 'supervisor' || isAdminOrDiretor()) && (
                      <form
                        className="cf-dist-form"
                        onSubmit={handleUsarLote}
                        style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-color)' }}
                      >
                        <div className="cf-form-group">
                          <label>Usar para mim (qtd)</label>
                          <input
                            type="number"
                            min={1}
                            max={estoqueDispCount || 1}
                            value={usarQtd}
                            onChange={(e) => setUsarQtd(e.target.value)}
                          />
                        </div>
                        <button
                          type="submit"
                          className="cf-btn-secondary"
                          disabled={usandoLote || estoqueDispCount === 0}
                        >
                          {usandoLote ? 'Ativando...' : 'Usar do estoque'}
                        </button>
                      </form>
                    )}

                    {user?.role === 'supervisor' && subordinados.length === 0 && (
                      <p className="cf-muted" style={{ marginTop: '1rem' }}>
                        Nenhum vendedor subordinado com acesso à página DID.
                      </p>
                    )}
                  </div>
                </div>

                <div className="cf-card">
                  <div className="cf-card-header">
                    <span className="cf-card-header-title">Itens no estoque</span>
                  </div>
                  <div className="cf-card-body">
                    <div className="cf-filters">
                      <input
                        type="search"
                        placeholder="Buscar número..."
                        value={estQ}
                        onChange={(e) => {
                          setEstQ(e.target.value);
                          setEstPage(1);
                        }}
                      />
                      <select
                        value={estValidade}
                        onChange={(e) => {
                          setEstValidade(e.target.value);
                          setEstPage(1);
                        }}
                      >
                        <option value="">Validade: todas</option>
                        <option value="vigentes">Vigentes</option>
                        <option value="expirados">Expirados</option>
                      </select>
                    </div>
                    {listLoading ? (
                      <p className="cf-muted">Carregando...</p>
                    ) : estoque.length === 0 ? (
                      <p className="cf-empty">Estoque vazio.</p>
                    ) : (
                      <>
                        <div className="cf-table-wrap">
                          <table className="cf-table">
                            <thead>
                              <tr>
                                <th>Usuário</th>
                                <th>Senha</th>
                                <th>Status</th>
                                <th>Válido até</th>
                                {isAdminOrDiretor() && <th>Dono</th>}
                                <th>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {estoque.map((n) => renderNumeroRow(n, { estoqueMode: true }))}
                            </tbody>
                          </table>
                        </div>
                        {renderPagination(estPage, estTotalPages, estTotal, setEstPage)}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {aba === 'admin-manual' && isAdminOrDiretor() && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Liberar números manualmente</span>
                </div>
                <div className="cf-card-body">
                  <p className="cf-muted" style={{ marginBottom: '1rem' }}>
                    Sem compra: escolha o usuário, cole os pares Usuário/Senha. Os 14 dias começam
                    no cadastro (também no estoque do supervisor). Enviar ou usar não reinicia o prazo.
                  </p>
                  <form className="cf-config-form" onSubmit={handleLiberarManual}>
                    <div className="cf-form-group full">
                      <label>Usuário de destino</label>
                      <select
                        value={manualDestinoId}
                        onChange={(e) => setManualDestinoId(e.target.value)}
                        required
                      >
                        <option value="">Selecione...</option>
                        {destinosManual.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.nome} ({u.role})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="cf-form-group full">
                      <label>Modo</label>
                      <select value={manualModo} onChange={(e) => setManualModo(e.target.value)}>
                        <option value="auto">Automático (vendedor = ativo / supervisor = estoque, ambos com 14 dias)</option>
                        <option value="ativo">Forçar ativo (também para supervisor)</option>
                      </select>
                    </div>
                    <div className="cf-form-group full">
                      <label>Números (Usuário / Senha)</label>
                      <textarea
                        rows={10}
                        placeholder={'Usuário\n4831981705\n\nSenha\n9srpt'}
                        value={manualTexto}
                        onChange={(e) => setManualTexto(e.target.value)}
                        required
                      />
                    </div>
                    <button type="submit" className="cf-btn-primary" disabled={liberandoManual}>
                      {liberandoManual ? 'Liberando...' : 'Liberar números'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {aba === 'admin-pedidos' && isAdminOrDiretor() && (
              <div className="cf-lista">
                <div className="cf-card">
                  <div className="cf-card-body">
                    <div className="cf-filters">
                      <input
                        type="search"
                        placeholder="Buscar ID, solicitante..."
                        value={ordQ}
                        onChange={(e) => {
                          setOrdQ(e.target.value);
                          setOrdPage(1);
                        }}
                      />
                      <select
                        value={ordStatus}
                        onChange={(e) => {
                          setOrdStatus(e.target.value);
                          setOrdPage(1);
                        }}
                      >
                        <option value="">Todos os status</option>
                        {Object.entries(STATUS_ORDEM).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {listLoading && <p className="cf-muted">Carregando...</p>}
                {!listLoading && ordens.length === 0 ? (
                  <div className="cf-card">
                    <div className="cf-card-body cf-empty">Nenhum pedido.</div>
                  </div>
                ) : (
                  ordens.map((o) => renderPedidoCard(o, true))
                )}
                {ordens.length > 0 &&
                  renderPagination(ordPage, ordTotalPages, ordTotal, setOrdPage)}
              </div>
            )}

            {aba === 'admin-senhas' && isAdminOrDiretor() && (
              <div className="cf-lista">
                <div className="cf-card">
                  <div className="cf-card-body">
                    <div className="cf-filters">
                      <input
                        type="search"
                        placeholder="Buscar número..."
                        value={senQ}
                        onChange={(e) => {
                          setSenQ(e.target.value);
                          setSenPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
                {listLoading && <p className="cf-muted">Carregando...</p>}
                {!listLoading && pendentesSenha.length === 0 ? (
                  <div className="cf-card">
                    <div className="cf-card-body cf-empty">Nenhuma troca de senha pendente.</div>
                  </div>
                ) : (
                  pendentesSenha.map((n) => (
                    <div key={n.id} className="cf-pedido-card">
                      <div className="cf-pedido-header">
                        <div>
                          <strong>{n.usuario}</strong>
                          <span className="cf-muted"> — {n.dono?.nome}</span>
                        </div>
                        <span className="cf-badge cf-badge-warn">Pendente</span>
                      </div>
                      <p className="cf-muted">
                        Solicitado em {formatDateTime(n.solicitacao_senha_em)}
                      </p>
                      <label>Colar novas credenciais</label>
                      <textarea
                        rows={5}
                        placeholder={`Usuário\n${n.usuario}\n\nSenha\nnovaSenha`}
                        value={textoSenha[n.id] || ''}
                        onChange={(e) =>
                          setTextoSenha((prev) => ({ ...prev, [n.id]: e.target.value }))
                        }
                      />
                      <button
                        type="button"
                        className="cf-btn-primary"
                        disabled={enviandoSenha === n.id}
                        onClick={() => handleAtualizarSenha(n.id)}
                      >
                        {enviandoSenha === n.id ? 'Salvando...' : 'Atualizar senha'}
                      </button>
                    </div>
                  ))
                )}
                {pendentesSenha.length > 0 &&
                  renderPagination(senPage, senTotalPages, senTotal, setSenPage)}
              </div>
            )}

            {aba === 'config' && isAdminOrDiretor() && (
              <div className="cf-card">
                <div className="cf-card-header">
                  <span className="cf-card-header-title">Configuração</span>
                </div>
                <div className="cf-card-body">
                  <form className="cf-config-form" onSubmit={handleSalvarConfig}>
                    <div className="cf-form-group">
                      <label>Preço unitário (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={configForm.preco_unitario}
                        onChange={(e) =>
                          setConfigForm((prev) => ({ ...prev, preco_unitario: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="cf-form-group">
                      <label>Tipo da chave PIX</label>
                      <input
                        type="text"
                        value={configForm.chave_pix_tipo}
                        onChange={(e) =>
                          setConfigForm((prev) => ({ ...prev, chave_pix_tipo: e.target.value }))
                        }
                      />
                    </div>
                    <div className="cf-form-group">
                      <label>Chave PIX</label>
                      <input
                        type="text"
                        value={configForm.chave_pix}
                        onChange={(e) =>
                          setConfigForm((prev) => ({ ...prev, chave_pix: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="cf-form-group full">
                      <label>Texto da página</label>
                      <textarea
                        rows={8}
                        value={configForm.texto_pagina}
                        onChange={(e) =>
                          setConfigForm((prev) => ({ ...prev, texto_pagina: e.target.value }))
                        }
                      />
                    </div>
                    <button type="submit" className="cf-btn-primary" disabled={salvandoConfig}>
                      {salvandoConfig ? 'Salvando...' : 'Salvar configuração'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ComoFunciona;
